import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { Integration, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { CapsuleButton } from "../../../Everou/_components";
import { Icons } from "../../../Everou/_assets";
import { useError } from "../../../Everou/_hooks/useError";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { IntegrationSectionTitle } from "../../../Everou/IntegrationsModule/components/IntegrationSectionTitle";
import SendInvitationURLToggle from "../../../Everou/IntegrationsModule/components/SendInvitationURLToggle";
import CreatePublicUrlToggle from "../../../Everou/InvitationsModule/CreatePublicUrlToggle";
import { useIntegrationName } from "./useIntegrationName";

type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onToggleEditable: (index: number) => void;
    onDelete: (index: number) => void;
    index: number;
    // True once the owner has another Manual account: from then on the name is
    // the only thing telling them apart. On the first one it is optional.
    nameRequired?: boolean;
};

/**
 * The whole Manual template form: a name and the two switches that change what
 * creating a reservation by hand does.
 *
 * There is no credentials section because there is no account: nothing is
 * validated against anyone, and no check-in/check-out or lead time either --
 * the manager types the dates of each reservation.
 */
export function PMSManualIntegrationInput({
    integration,
    onToggleEditable,
    onDelete,
    index,
    nameRequired = false,
}: IntegrationInputProps) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    // One Save for the whole form: this company's save validates nothing and
    // mints nothing, so the name has no reason to be saved on its own.
    const { templateName, nameChanged, nameInput } = useIntegrationName(
        integration, { required: nameRequired, standaloneSave: false });
    const { errorMessage, setError, hasError } = useError();

    // Read through the same defaults the state is seeded with. Comparing the
    // state against the RAW field would leave Save enabled forever on a saved,
    // read-only card whenever the server omits one of them (false !== undefined).
    const storedCreatePublicUrl = integration.createPublicUrl ?? true;
    const storedSendInvitationURL = integration.sendInvitationURL ?? false;

    const [createPublicUrl, setCreatePublicUrl] = useState(storedCreatePublicUrl);
    const [sendInvitationURL, setSendInvitationURL] = useState(storedSendInvitationURL);

    useEffect(() => {
        setCreatePublicUrl(integration.createPublicUrl ?? true);
        setSendInvitationURL(integration.sendInvitationURL ?? false);
    }, [integration]);

    // Anything the Save would persist. The name counts: unlike the other
    // platforms, this form has no separate rename button, so the name travels
    // in the same PUT as the toggles and editing it IS a pending change.
    const hasChanges = !integration.uid
        || nameChanged
        || createPublicUrl !== storedCreatePublicUrl
        || sendInvitationURL !== storedSendInvitationURL;

    const canSave = useMemo(() => {
        if (hasError) return false;
        // With a sibling account, the name is the only thing telling them
        // apart, so the backend rejects an empty one -- don't offer a Save that
        // is going to 400.
        if (nameRequired && !templateName) return false;
        return hasChanges;
    }, [hasError, nameRequired, templateName, hasChanges]);

    const onSave = useCallback(async () => {
        const editedIntegration: Integration = {
            name: templateName,
            company: integration.company,
            uid: integration.uid,
            createPublicUrl,
            sendInvitationURL,
        };

        try {
            await dispatch(PMSIntegrationsActions.createIntegration(editedIntegration));
        } catch (err: any) {
            setError(err.message);
        }
    }, [dispatch, setError, templateName, integration.company,
        integration.uid, createPublicUrl, sendInvitationURL]);

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            {nameInput}
            <div className="col-span-full">
                <p className="small text-muted mb-0">{t("mod_integrations_manual_description")}</p>
            </div>
            <div className="col-span-full mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_delivery")} />
            </div>
            <div className="col-span-full">
                <CreatePublicUrlToggle
                    boldedName={false}
                    initialState={createPublicUrl}
                    readOnly={!integration.editable}
                    onChange={setCreatePublicUrl}
                />
                <div className="mt-3">
                    <SendInvitationURLToggle
                        optional
                        boldedName={false}
                        initialState={sendInvitationURL}
                        readOnly={!integration.editable}
                        onChange={setSendInvitationURL}
                        // The shared wording says "email or SMS", true of the
                        // PMS-synced path (it falls back to SMS when the PMS
                        // gives no email but gives a phone). Creating a
                        // reservation by hand only ever sends an email.
                        label={t("mod_integrations_manual_send_email")}
                        tooltip={t("mod_integrations_manual_send_email_tooltip")}
                    />
                </div>
            </div>
            <div className="col-span-full mt-4 d-flex justify-content-between align-items-center">
                <div className="d-flex">
                    {integration.uid &&
                        <CapsuleButton
                            size="SMALL"
                            classNameExtra="mr-2"
                            style={!integration.editable ? "PRIMARY" : "INFO"}
                            onClick={() => onToggleEditable(index)}
                            text={t(!integration.editable ? "global_edit" : "global_cancel")}
                        />
                    }
                    {integration.uid &&
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    }
                </div>
                {(!integration.uid || integration.editable) && hasChanges && (
                    <CapsuleButton
                        size="SMALL"
                        isEnabled={canSave}
                        onClick={onSave}
                        text={t("global_save")}
                    />
                )}
            </div>
            {errorMessage && (
                <div className="text-danger mr-2">{errorMessage}</div>
            )}
        </div>
    );
}
